// src/config.ts — central, fail-closed app configuration (R-20).
//
// These are PUBLIC identifiers (tenant/client IDs, hostnames) — not secrets —
// but hardcoded fallbacks cause config drift and unsafe deploys, so none exist.
// Values are injected at build time from CI variables (REACT_APP_*). If a
// required value is missing or is a placeholder, this module THROWS and the
// app does not start: config fails CLOSED.

const PLACEHOLDER_PATTERN =
  /^$|your[-_]|changeme|placeholder|<[^>]*>|example|xxxx|todo/i;

function required(name: string, value: string | undefined): string {
  if (!value || PLACEHOLDER_PATTERN.test(value)) {
    throw new Error(
      `Configuration error: ${name} is missing or a placeholder. ` +
        `Set it as a build-time environment variable (R-20: config fails closed).`
    );
  }
  return value;
}

export const isLocalDevelopment =
  typeof window !== "undefined" && window.location.hostname === "localhost";

export const TENANT_ID = required("REACT_APP_TENANT_ID", process.env.REACT_APP_TENANT_ID);
export const CLIENT_ID = required("REACT_APP_CLIENT_ID", process.env.REACT_APP_CLIENT_ID);

// appId of the "FDD Platform API" Entra registration — the audience the SPA
// requests tokens for when calling the Express backend (R-02).
export const FDD_API_CLIENT_ID = required(
  "REACT_APP_FDD_API_CLIENT_ID",
  process.env.REACT_APP_FDD_API_CLIENT_ID
);

// The deployed frontend origin. Only consumed outside local dev; still fails
// closed there so a production build can never ship without it.
export function getProdBaseUrl(): string {
  const value = required("REACT_APP_PROD_URL", process.env.REACT_APP_PROD_URL);
  if (/localhost|127\.0\.0\.1/i.test(value)) {
    throw new Error(
      "Configuration error: REACT_APP_PROD_URL points at localhost (R-20: config fails closed)."
    );
  }
  return value;
}

export const BASE_URL = isLocalDevelopment ? "http://localhost:3000" : getProdBaseUrl();
